import { NavIcon } from './NavIcons';
import './Navigation.css';

interface NavItem {
  id: string;
  label: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Design',
    items: [{ id: 'project-designer', label: 'Project Designer' }],
  },
  {
    label: 'Calculators',
    items: [
      { id: 'motor', label: 'Motor Calc' },
      { id: 'transformer', label: 'Transformer' },
      { id: 'generator', label: 'Generator' },
      { id: 'ampacity', label: 'Wire Ampacity' },
      { id: 'feeder', label: 'Feeder/Service Sizing' },
      { id: 'conduit', label: 'Conduit Sizing' },
      { id: 'voltage-drop', label: 'Voltage Drop' },
      { id: 'multi-motor-feeder', label: 'Multi-Motor Feeder' },
      { id: 'hazardous-locations', label: 'Hazardous Locations' },
    ],
  },
  {
    label: 'Schedules',
    items: [
      { id: 'panel-schedule', label: 'Panel Schedule' },
      { id: 'load-analysis', label: 'Load Analysis' },
      { id: 'plc-io', label: 'PLC I/O List' },
    ],
  },
];

/** Admin-only group — appended when the whoami probe confirms ADMIN_USERS. */
const ADMIN_GROUP: NavGroup = {
  label: 'Administration',
  items: [{ id: 'admin', label: 'Admin' }],
};

interface NavigationProps {
  activeSection: string;
  onNavigate: (section: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  isCollapsed?: boolean;
  onCollapse?: () => void;
  /** Show the Admin dashboard entry (only when the signed-in user is an admin). */
  showAdmin?: boolean;
}

export function Navigation({
  activeSection,
  onNavigate,
  isOpen,
  onToggle,
  isCollapsed = false,
  onCollapse,
  showAdmin = false,
}: NavigationProps) {
  const groups = showAdmin ? [...NAV_GROUPS, ADMIN_GROUP] : NAV_GROUPS;
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="nav-overlay" onClick={onToggle} aria-hidden="true" />
      )}

      <nav
        className={`navigation ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}
        aria-label="Main navigation"
      >
        <div className="nav-header">
          <span className="nav-title">Menu</span>
          <div className="nav-header-buttons">
            {onCollapse && (
              <button
                className="nav-collapse-btn"
                onClick={onCollapse}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isCollapsed ? (
                    <path d="M13 17l5-5-5-5M6 17l5-5-5-5" />
                  ) : (
                    <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
                  )}
                </svg>
              </button>
            )}
            <button
              className="nav-close-btn"
              onClick={onToggle}
              aria-label="Close menu"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="nav-scroll">
          {groups.map((group) => (
            <section key={group.label} className="nav-group" aria-label={group.label}>
              <div className="nav-group-label">{group.label}</div>
              <ul className="nav-list">
                {group.items.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        className={`nav-item ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          onNavigate(item.id);
                          if (window.innerWidth < 768) {
                            onToggle();
                          }
                        }}
                        title={isCollapsed ? item.label : undefined}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <span className="nav-icon">
                          <NavIcon id={item.id} />
                        </span>
                        <span className="nav-label">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <div className="nav-footer">
          <span className="nav-version">{isCollapsed ? 'v1.0' : 'BGD Electrical Calc v1.0'}</span>
        </div>
      </nav>
    </>
  );
}

export default Navigation;
